import { useEffect, useState } from 'react';
import { viewportSize } from '../../lib/viewport.js';

function detectTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/** True sur un device tactile réel (pas seulement un petit écran). */
export function useIsTouchDevice(): boolean {
  const [isTouch, setIsTouch] = useState(detectTouchDevice);

  useEffect(() => {
    const update = () => setIsTouch(detectTouchDevice());
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return isTouch;
}

/**
 * True quand le pointeur principal est le doigt (téléphone, tablette). Faux sur un
 * ordinateur portable à écran tactile, où la souris reste le pointeur principal
 * alors que `maxTouchPoints > 0` (useIsTouchDevice vrai).
 */
export function usePrimaryTouch(): boolean {
  const query = '(pointer: coarse)';
  const [coarse, setCoarse] = useState(() => window.matchMedia?.(query).matches ?? false);

  useEffect(() => {
    const mql = window.matchMedia?.(query);
    if (!mql) return;
    const update = () => setCoarse(mql.matches);
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  return coarse;
}

function getViewportWidth(): number {
  return viewportSize().width;
}

/** True si la largeur de viewport est sous le breakpoint (mobile/tablette). */
export function useIsMobile(breakpointPx = 768): boolean {
  const [isMobile, setIsMobile] = useState(() => getViewportWidth() <= breakpointPx);

  useEffect(() => {
    const update = () => setIsMobile(getViewportWidth() <= breakpointPx);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, [breakpointPx]);

  return isMobile;
}
